import type { DownlineQuery, DownlineResult } from '@/data/api'
import type { Account } from '@/data/types'
import { agentNodeToAccount, type AgentNode } from '@/api/map/agent'

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** Attach childCount / memberCount from adjacency in the flat tree. */
export function enrichTreeAccounts(nodes: AgentNode[]): Account[] {
  const accounts = nodes.map(agentNodeToAccount)
  const byParent = new Map<string | null, Account[]>()
  for (const a of accounts) {
    const key = a.parentId
    const list = byParent.get(key) ?? []
    list.push(a)
    byParent.set(key, list)
  }
  for (const a of accounts) {
    const kids = byParent.get(a.id) ?? []
    a.childCount = kids.length
    a.memberCount =
      a.role === 'player'
        ? 0
        : kids.reduce((sum, k) => sum + (k.role === 'player' ? 1 : k.memberCount), 0)
  }
  // Second pass: members under agents that themselves have agents need rollup.
  // Tree is shallow enough for a few passes.
  for (let pass = 0; pass < 6; pass++) {
    for (const a of accounts) {
      if (a.role === 'player') continue
      const kids = byParent.get(a.id) ?? []
      a.memberCount = kids.reduce(
        (sum, k) => sum + (k.role === 'player' ? 1 : k.memberCount),
        0,
      )
    }
  }
  return accounts
}

/** Client-side filter / sort / page over hierarchy tree accounts. */
export function toDownlineResult(
  nodes: AgentNode[],
  rootId: string,
  query: DownlineQuery = {},
): DownlineResult {
  const {
    search = '',
    roles,
    statuses,
    hasExposure,
    needsAttention,
    settlement,
    directOnly = false,
    page = 1,
    pageSize = 25,
    sort = 'username',
    direction = 'asc',
  } = query

  const accounts = enrichTreeAccounts(nodes)
  const byId = new Map(accounts.map((a) => [a.id, a]))

  let rows = accounts.filter((a) => a.id !== rootId)
  if (directOnly) {
    rows = rows.filter((a) => a.parentId === rootId)
  } else {
    // Whole subtree under root (already scoped by API); drop anyone outside path.
    rows = rows.filter((a) => {
      if (a.parentId === rootId) return true
      let cur: Account | undefined = a
      const seen = new Set<string>()
      while (cur?.parentId) {
        if (cur.parentId === rootId) return true
        if (seen.has(cur.parentId)) break
        seen.add(cur.parentId)
        cur = byId.get(cur.parentId)
      }
      return false
    })
  }

  const needle = search.trim().toLowerCase()
  rows = rows
    .filter(
      (a) =>
        !needle ||
        a.username.toLowerCase().includes(needle) ||
        a.displayName.toLowerCase().includes(needle),
    )
    .filter((a) => !roles?.length || roles.includes(a.role))
    .filter((a) => !statuses?.length || statuses.includes(a.status))
    .filter((a) => !hasExposure || a.exposure > 0)
    .filter((a) => !needsAttention || a.flags.length > 0)
    // Mirrors `listDownline` exactly. If the fixture path and the live path
    // filtered differently, the same URL would show different rows depending on
    // VITE_API_BASE and a bulk plan built from one would not match the table
    // built from the other.
    .filter((a) =>
      !settlement ? true : settlement === 'collect' ? a.settlement > 0 : a.settlement < 0,
    )

  const factor = direction === 'asc' ? 1 : -1
  rows = [...rows].sort((a, b) => {
    switch (sort) {
      case 'username':
        return a.username.localeCompare(b.username, 'en-IN') * factor
      case 'lastActiveAt': {
        const av = a.lastActiveAt ? Date.parse(a.lastActiveAt) : -1
        const bv = b.lastActiveAt ? Date.parse(b.lastActiveAt) : -1
        return (av - bv) * factor
      }
      default:
        return ((a[sort] as number) - (b[sort] as number)) * factor
    }
  })

  const totals = rows.reduce(
    (acc, row) => {
      acc.creditLimit += row.creditLimit
      acc.balance += row.balance
      acc.exposure += row.exposure
      if (row.settlement > 0) acc.toCollect += row.settlement
      else acc.toPay += -row.settlement
      if (row.role === 'player') acc.members += 1
      else acc.agents += 1
      return acc
    },
    { creditLimit: 0, balance: 0, exposure: 0, toCollect: 0, toPay: 0, members: 0, agents: 0 },
  )

  const start = (page - 1) * pageSize
  return {
    rows: rows.slice(start, start + pageSize),
    total: rows.length,
    page,
    pageSize,
    totals: {
      creditLimit: round2(totals.creditLimit),
      balance: round2(totals.balance),
      exposure: round2(totals.exposure),
      toCollect: round2(totals.toCollect),
      toPay: round2(totals.toPay),
      members: totals.members,
      agents: totals.agents,
    },
  }
}
